import { createFileRoute, useNavigate } from "@tanstack/react-router";
import createAxios from "@/lib/axios";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { Subject } from "@/types";

export const Route = createFileRoute(
  "/dashboard/presentation/new/select-subject",
)({
  component: () => (
    <div className="flex size-full items-center">
      <SelectSubjects />
    </div>
  ),
  loader: async () => {
    const client = await createAxios();
    const { data } = await client.get<{
      subjects: Subject[];
    }>(`${import.meta.env.VITE_BACKEND_URL}/subjects`);
    return { subjects: data.subjects };
  },
});

function SelectSubjects() {
  const navigate = useNavigate();
  const { subjects } = Route.useLoaderData();

  const keys = subjects.map((subject) => subject.id.toString());
  const formSchema = z.object({
    subject: z.enum([keys[0], ...keys.slice(1)]),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    navigate({
      to: "/dashboard/presentation/new/select-unit",
      search: {
        subject_id: Number(data.subject),
      },
    });
  }

  return (
    <main className="mt-16 size-full p-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => onSubmit(data))}
          className="flex flex-col justify-center gap-8"
        >
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-4">
                <FormLabel className="text-5xl font-bold">
                  Select Subject
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col gap-2"
                  >
                    {subjects.map((subject) => (
                      <SubjectItem key={subject.name} subject={subject} />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <SubmitButton
            isLoading={
              form.formState.isSubmitting || form.formState.isSubmitted
            }
          />
        </form>
      </Form>
    </main>
  );
}

function SubmitButton({ isLoading }: { isLoading: boolean }) {
  return (
    <Button
      type="submit"
      className="w-48"
      disabled={isLoading}
      aria-label="select topics"
    >
      {isLoading && <Spinner />}
      Select Topics
    </Button>
  );
}

function SubjectItem({ subject }: { subject: Subject }) {
  return (
    <FormItem>
      <FormControl className="align-middle">
        <RadioGroupItem value={subject.id.toString()} />
      </FormControl>
      <FormLabel className="pl-2 align-middle text-lg font-normal">
        {subject.displayName}
      </FormLabel>
    </FormItem>
  );
}
